import type { Appointment, ConflictingAppointment } from '../wire';

/**
 * One intent, as it sits on the device (spec §8.2: "an ordered list of intents,
 * each with a client-generated ULID and an idempotency key").
 *
 * ## The two ids, and why there are two
 *
 * `id` is the ULID this device minted. It is sent as `client_intent_id` in the
 * body, and `core-api` echoes it on the response — which is how an optimistic
 * row finds out which server row it became (`intents.ts`, `withIntentId`).
 *
 * `idempotencyKey` is what the server keys the replay memo on. It is
 * **`intent:${id}` deliberately**, because that is the exact key `readIntent`
 * derives when a request carries only `client_intent_id`. Sending both with that
 * relationship means the header and the body can never disagree — `readIntent`
 * says the header wins and that "a device that sets them to different values has
 * a bug" — and it means a retry that loses the header still hits the same memo.
 *
 * It is minted **once, at enqueue**, and stored. Every attempt reuses it. That
 * single fact is what makes replay safe: attempt three of an intent whose
 * attempt one committed gets the original response back, byte for byte, rather
 * than a second booking.
 *
 * ## `describe` is a snapshot, not a lookup
 *
 * The task list has to say *"Reschedule Dana?"* while the iPad is still in
 * airplane mode, and possibly for a client whose row was never mirrored. So the
 * human-facing facts are copied onto the intent when it is created. Reading them
 * back later out of a mirror that may have moved on would be a small merge, and
 * §8.3 does not do merges.
 */

export type IntentKind =
  | 'appointment.create'
  | 'appointment.reschedule'
  | 'appointment.cancel'
  | 'appointment.status'
  | 'client.create'
  | 'client.update'
  | 'client.archive'
  | 'client.identity.add'
  | 'client.identity.remove'
  | 'preference.create'
  | 'preference.update'
  | 'preference.delete'
  | 'service.create'
  | 'service.update'
  | 'hours.set'
  | 'blackout.create'
  | 'blackout.delete';

export type IntentStatus = 'queued' | 'inflight' | 'done' | 'rejected';

export interface IntentDescription {
  /** "Book Dana — Swedish Massage", "Edit Dana Whitfield". Shown as-is. */
  what: string;
  /** The guest, when there is one. The subject of the task sentence. */
  person?: string | undefined;
  /** The instant the intent is about, for "This 2 PM…". */
  whenIso?: string | undefined;
}

export interface RejectionRecord {
  code: string;
  message: string;
  status: number;
  detail?: Record<string, unknown> | undefined;
  conflicts?: ConflictingAppointment[] | undefined;
  at: string;
}

export interface OutboxEntry {
  /** The client ULID. Sent as `client_intent_id`. */
  id: string;
  /** Total order. Replay follows it exactly (spec §8.2: "in order"). */
  seq: number;
  kind: IntentKind;
  /** Path parameters the request needs — appointment id, client id, and so on. */
  target: Record<string, string>;
  payload: Record<string, unknown>;
  /** `intent:${id}`. Stable across every attempt, forever. */
  idempotencyKey: string;
  createdAt: string;
  status: IntentStatus;
  attempts: number;
  /** Epoch ms; the drain will not touch this entry before it. */
  nextAttemptAt: number;
  describe: IntentDescription;
  /**
   * The row to show immediately, before the server has answered. Only creates
   * have one — an edit shows through the mirrored row it edits.
   */
  optimistic?: Appointment | undefined;
  lastError?: string | undefined;
  rejection?: RejectionRecord | undefined;
  settledAt?: string | undefined;
}

/** Queued or in flight: work the device still owes the cloud. */
export function isPending(entry: OutboxEntry): boolean {
  return entry.status === 'queued' || entry.status === 'inflight';
}
